import {hslToColorString} from 'polished';
import {HslaColor} from 'polished/lib/types/color';

import {collectEnumValues} from '../../../helpers/src/typescript/enumHelpers';
import {parseToHsla} from './colorHelpers';

interface ComputedStyles {
  backgroundColor: string;
  color: string;
  borderBottomColor: string;
  borderBottomStyle: string;
  borderBottomWidth: string;
  borderLeftColor: string;
  borderLeftStyle: string;
  borderLeftWidth: string;
  borderRightColor: string;
  borderRightStyle: string;
  borderRightWidth: string;
  borderTopColor: string;
  borderTopStyle: string;
  borderTopWidth: string;
}

enum BorderDirectionsEnum {
  TOP = 'top',
  LEFT = 'left',
  RIGHT = 'right',
  BOTTOM = 'bottom',
}

const borderStyleNames = {
  [BorderDirectionsEnum.TOP]: {
    widthCssName: 'borderTopWidth',
    colorCssName: 'borderTopColor',
    styleCssName: 'borderTopStyle',
  },
  [BorderDirectionsEnum.BOTTOM]: {
    widthCssName: 'borderBottomWidth',
    colorCssName: 'borderBottomColor',
    styleCssName: 'borderBottomStyle',
  },
  [BorderDirectionsEnum.LEFT]: {
    widthCssName: 'borderLeftWidth',
    colorCssName: 'borderLeftColor',
    styleCssName: 'borderLeftStyle',
  },
  [BorderDirectionsEnum.RIGHT]: {
    widthCssName: 'borderRightWidth',
    colorCssName: 'borderRightColor',
    styleCssName: 'borderRightStyle',
  },
} as const;

enum CssPropertiesEnum {
  BACKGROUND_COLOR = 'background-color',
  COLOR = 'color',
  BORDER_BOTTOM_COLOR = 'border-bottom-color',
  BORDER_TOP_COLOR = 'border-top-color',
  BORDER_RIGHT_COLOR = 'border-right-color',
  BORDER_LEFT_COLOR = 'border-left-color',
}

const borderDirectionToCssPropertyMap = {
  [BorderDirectionsEnum.TOP]: CssPropertiesEnum.BORDER_TOP_COLOR,
  [BorderDirectionsEnum.BOTTOM]: CssPropertiesEnum.BORDER_BOTTOM_COLOR,
  [BorderDirectionsEnum.RIGHT]: CssPropertiesEnum.BORDER_RIGHT_COLOR,
  [BorderDirectionsEnum.LEFT]: CssPropertiesEnum.BORDER_LEFT_COLOR,
} as const;

const computeInvertedBackgroundColor = (hslColor: HslaColor, appForegroundColor: string) => {
  // If the color is a light grey, clamp it to our background color.
  if (hslColor.lightness > 0.6 && hslColor.saturation < 0.3) {
    return appForegroundColor;
  }

  // Highlights (background with high transparency) should be color inverted.
  if (hslColor.alpha !== undefined && hslColor.alpha < 0.5) {
    return hslToColorString({
      ...hslColor,
      lightness: 1 - hslColor.lightness,
    });
  }

  // Otherwise, dark backgrounds with colors should be set to a low lightness.
  return hslToColorString({
    ...hslColor,
    lightness: 0.1,
  });
};

// Color refers to the literal "color" CSS property, not any color.
const computeInvertedColor = (hslColor: HslaColor) =>
  hslToColorString({
    ...hslColor,
    // Colors tend to look a bit desaturated when converted from dark to light.
    saturation: Math.min(1, hslColor.saturation * 1.2),
    // Colors which look fine on light can lack contrast when inverted on dark backgrounds.
    // Pure white also doesn't look good, so we clamp the lightness between two values.
    lightness: Math.max(0.7, Math.min(0.98, 1 - hslColor.lightness)),
  });

const computeInvertedBorderColor = (hslColor: HslaColor) =>
  hslToColorString({
    ...hslColor,
    // Inverted borders tend to be too bright, so we limit the lightness.
    lightness: Math.max(0.3, Math.min(0.6, 1 - hslColor.lightness)),
  });

type ColorFinder = (hslColor: HslaColor, appForegroundColor: string) => string;

const colorFinder: Record<CssPropertiesEnum, ColorFinder> = {
  [CssPropertiesEnum.BACKGROUND_COLOR]: computeInvertedBackgroundColor,
  [CssPropertiesEnum.COLOR]: computeInvertedColor,
  [CssPropertiesEnum.BORDER_BOTTOM_COLOR]: computeInvertedBorderColor,
  [CssPropertiesEnum.BORDER_LEFT_COLOR]: computeInvertedBorderColor,
  [CssPropertiesEnum.BORDER_RIGHT_COLOR]: computeInvertedBorderColor,
  [CssPropertiesEnum.BORDER_TOP_COLOR]: computeInvertedBorderColor,
};

const invertNodeColor = (
  node: HTMLElement,
  property: CssPropertiesEnum,
  color: string,
  appForegroundColor: string,
) => {
  const hslColor = parseToHsla(color);

  // The browser returns rgb(0, 0, 0, 0) if computed styles are not set. Skip the transparent case.
  if (hslColor.alpha === 0) {
    return;
  }

  // Note: The browser setProperty API uses hypenated CSS names (e.g. border-color not borderColor.)
  node.style.setProperty(property, colorFinder[property](hslColor, appForegroundColor), 'important');
};

// getComputedStyle is forced to perform style recalculation if we alternate
// reads and writes. Since the getComputedStyle object is live (reads are done
// just in time,) we need to store the values instead of using the getComputedStyle
// object directly.
const findComputedStylesForNode = (node: HTMLElement): ComputedStyles => {
  const styles = getComputedStyle(node);
  return {
    backgroundColor: styles.backgroundColor,
    color: styles.color,
    borderBottomColor: styles.borderBottomColor,
    borderBottomStyle: styles.borderBottomStyle,
    borderBottomWidth: styles.borderBottomWidth,
    borderLeftColor: styles.borderLeftColor,
    borderLeftStyle: styles.borderLeftStyle,
    borderLeftWidth: styles.borderLeftWidth,
    borderRightColor: styles.borderRightColor,
    borderRightStyle: styles.borderRightStyle,
    borderRightWidth: styles.borderRightWidth,
    borderTopColor: styles.borderTopColor,
    borderTopStyle: styles.borderTopStyle,
    borderTopWidth: styles.borderTopWidth,
  };
};

// Whether the border is rendered by the browser.
const isBorderVisible = (width: string, style: string) => width !== '0px' && style !== 'none';

interface InvertNodeColorsParams {
  node: HTMLElement;
  styles: ComputedStyles;
  appForegroundColor: string;
  hasTextNodeChild: boolean;
}

const invertNodeColors = ({node, styles, appForegroundColor, hasTextNodeChild}: InvertNodeColorsParams) => {
  invertNodeColor(node, CssPropertiesEnum.BACKGROUND_COLOR, styles.backgroundColor, appForegroundColor);

  // If an element doesn't have any direct descendants which are
  // text nodes, skip color inversion.
  if (hasTextNodeChild) {
    invertNodeColor(node, CssPropertiesEnum.COLOR, styles.color, appForegroundColor);
  }

  // Iterate over top, left, right, and bottom border values.
  for (const direction of collectEnumValues(BorderDirectionsEnum)) {
    // This looks up the CSS names (e.g. borderBottomWidth)
    const {widthCssName, styleCssName, colorCssName} = borderStyleNames[direction];

    // These are the computed style values.
    const borderWidth = styles[widthCssName];
    const borderStyle = styles[styleCssName];
    const borderColor = styles[colorCssName];
    if (isBorderVisible(borderWidth, borderStyle)) {
      // Need to map camel case names to hyphenated CSS names.
      invertNodeColor(node, borderDirectionToCssPropertyMap[direction], borderColor, appForegroundColor);
    }
  }
};

// For nodes which already have a dark background, we skip traversing into them.
// This has some edge cases (e.g. if there's a light background nested within a
// dark background) but that's an uncommon enough case where this generally has
// good results, and in that case it just remains the way it looked originally.
const shouldSkipSubtree = (backgroundColor: string) => {
  const {lightness, alpha} = parseToHsla(backgroundColor);
  return lightness < 0.6 && alpha === 1;
};

// We're doing a recursive traversal, where style reads are done
// preorder and writes are done postorder. This ensures reads all
// happen before any writes do, which minimizes style recalculation.
const recursivelyUpdateNodeColors = (node: HTMLElement, appForegroundColor: string) => {
  // Preorder: Style reads. No writes.
  const styles = findComputedStylesForNode(node);

  if (shouldSkipSubtree(styles.backgroundColor)) {
    return;
  }

  // Recursive traversal. We keep track of whether this node has any text children
  // as an optimization. Nodes with no text children do not require color inversion.
  let hasTextNodeChild = false;
  for (const child of Array.from(node.childNodes)) {
    if (child instanceof HTMLElement) {
      recursivelyUpdateNodeColors(child, appForegroundColor);
    } else if (child.nodeType === Node.TEXT_NODE) {
      hasTextNodeChild = true;
    }
  }

  // Postorder: Style writes. No reads.
  invertNodeColors({
    node,
    styles,
    hasTextNodeChild,
    appForegroundColor,
  });
};

const isTreeOverMaxSize = (node: HTMLElement) => {
  // Roughly 25 nodes can be processed per ms on an M1 MacBook. 500 nodes would
  // add ~20ms, and should still be fast enough on slower hardware.
  const maxSize = 500;

  // TreeWalker API is fast (~1m ops/s.) This count function takes sub 0.1ms to run.
  const tree = document.createTreeWalker(node, NodeFilter.SHOW_ELEMENT);
  let count = 0;
  while (count < maxSize && tree.nextNode()) {
    count++;
  }
  return count === maxSize;
};

// Primary export. Use this function to convert a DOM tree to dark mode.
// Returns true if the DOM tree was converted to dark mode, false if conversion was skipped
// due to the size of the tree.
export const convertToDarkMode = (root: HTMLElement, appForegroundColor: string) => {
  if (isTreeOverMaxSize(root)) {
    return false;
  }
  recursivelyUpdateNodeColors(root, appForegroundColor);
  return true;
};
